import React, { useEffect, useMemo, useState } from 'react';
import { careRecipientUserId } from './FamilienkontoListHelpers';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Avatar, Drawer } from '../../../components/ui';
import { KontakteTab } from '../tabs/KontakteTab';
import { AufgabenNotizenTab } from '../tabs/AufgabenNotizenTab';
import { Gespraechsprotokolle } from '../../../components/calls/Gespraechsprotokolle';
import { Icon } from '../../../components/icons';
import { getThemeColors } from '../../../lib/theme';
import type { V1Admin, V1User } from '../../../lib/v1';
import { v2FamilyAccounts, type FamilyAccount } from '../../../lib/familyAccounts';

// ─── Kompakter Vorschau-Drawer (/pflege-Liste) ──────────────────────────────
//
// Reiter (23.10.2026): Aufgaben (offene, mit „Erledigte anzeigen"),
// Gesprächsprotokolle (Telefonate aller Konto-Mitglieder inkl. Transkripte)
// und Kontakte. Übersicht, Notizen und Schnell-Bearbeiten sind entfallen;
// Kontoname, Zuständigkeit und Notiz werden in der Konto-Detailseite
// bearbeitet („Bearbeiten" im Fuß des Drawers).

export type FamilienkontoDrawerTab = 'aufgaben' | 'gespraechsprotokolle' | 'kontakte';
type DrawerTabId = FamilienkontoDrawerTab;

export interface FamilienkontoDrawerProps {
  account: FamilyAccount;
  initialTab: FamilienkontoDrawerTab;
  dark: boolean;
  /** Nicht mehr genutzt (Schnell-Bearbeiten entfallen); bleibt für bestehende Aufrufer erhalten. */
  admins?: V1Admin[];
  onClose: () => void;
  onEdit: () => void;
  /** Nicht mehr genutzt (der Drawer speichert nichts mehr). */
  onSaved?: (updated: FamilyAccount) => void;
}

export function FamilienkontoDrawer({ account, initialTab, dark, onClose, onEdit }: FamilienkontoDrawerProps) {
  const { t: tr } = useTranslation();
  const t = getThemeColors(dark);
  const BRAND = '#7f56d9';
  const [tab, setTab] = useState<DrawerTabId>(initialTab);

  // Aufgaben und Gesprächsprotokolle hängen an einer Person des Kontos
  // (Konto-Modus: das Backend liefert dann die Einträge ALLER Mitglieder).
  // Die Listen-Zeile enthält die Mitglieder nicht immer, dann nachladen.
  const [anchorUserId, setAnchorUserId] = useState<number | null>(
    () => careRecipientUserId(account) ?? account.members?.find(m => m.user_id != null)?.user_id ?? null,
  );
  useEffect(() => {
    if (anchorUserId != null) return;
    let alive = true;
    v2FamilyAccounts.get(account.id)
      .then(full => {
        if (!alive) return;
        const id = careRecipientUserId(full) ?? full.members?.find(m => m.user_id != null)?.user_id ?? null;
        setAnchorUserId(id);
      })
      .catch(() => { /* ohne Person bleibt der Hinweis stehen */ });
    return () => { alive = false; };
  }, [account.id, anchorUserId]);

  // Pseudo-User für den Konto-Modus des Aufgaben-Reiters.
  const taskUser = useMemo(
    () => (anchorUserId != null ? ({ id: anchorUserId, family_account_id: account.id } as unknown as V1User) : null),
    [anchorUserId, account.id],
  );

  const careNames = account.care_recipients?.length ? account.care_recipients.join(', ') : null;

  return (
    <Drawer open onClose={onClose} width={560} dark={dark}>
      {/* Header */}
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 14 }}>
          <Avatar name={account.name || `#${account.id}`} userId={careRecipientUserId(account)} size={52} dark={dark} />
          <div>
            <div style={{ fontSize: 11, color: t.muted, fontWeight: 600 }}>{tr('pflege.familienkonto.detail.drawer.account_id', { id: account.id })}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: t.heading, marginTop: 2 }}>{account.name || tr('pflege.familienkonto.detail.drawer.account_id', { id: account.id })}</div>
            {careNames && <div style={{ fontSize: 12, color: t.muted, marginTop: 3 }}>{careNames}</div>}
            <div style={{ marginTop: 8 }}>
              <Badge tone="neutral" dark={dark}>{account.members_count} {account.members_count === 1 ? tr('pflege.tasks.person_label') : tr('pflege.familienkonto.detail.members_section')}</Badge>
            </div>
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6 }}>
          <Icon.Close c={t.muted} size={18} />
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, padding: '0 24px', borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
        {([
          { k: 'aufgaben', l: tr('pflege.detail.tab.aufgaben') },
          { k: 'gespraechsprotokolle', l: tr('pflege.detail.tab.gespraechsprotokolle') },
          { k: 'kontakte', l: tr('pflege.detail.tab.kontakte') },
        ] as const).map(x => (
          <button key={x.k} onClick={() => setTab(x.k)} style={{
            padding: '12px 4px', background: 'transparent', border: 'none',
            borderBottom: tab === x.k ? `2px solid ${BRAND}` : '2px solid transparent',
            color: tab === x.k ? BRAND : t.muted, fontSize: 13, fontWeight: 500,
            cursor: 'pointer', marginRight: 12, fontFamily: 'Outfit, sans-serif',
          }}>{x.l}</button>
        ))}
      </div>

      {/* Body */}
      <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
        {tab === 'aufgaben' && (
          taskUser
            ? <AufgabenNotizenTab user={taskUser} dark={dark} only="tasks" />
            : <div style={{ fontSize: 13, color: t.muted, padding: '8px 0' }}>{tr('pflege.tasks.drawer_no_user')}</div>
        )}

        {tab === 'gespraechsprotokolle' && (
          <Gespraechsprotokolle userId={anchorUserId} dark={dark} variant="plain" maxHeight={0} />
        )}

        {tab === 'kontakte' && (
          <KontakteTab accountId={account.id} dark={dark} />
        )}
      </div>

      <div style={{ padding: 14, borderTop: `1px solid ${t.border}`, display: 'flex', gap: 8, background: t.cardMuted, flexShrink: 0 }}>
        <Button variant="primary" dark={dark} style={{ flex: 1, justifyContent: 'center' }} icon={<Icon.Edit c="#fff" />} onClick={onEdit}>
          {tr('common.action.edit')}
        </Button>
      </div>
    </Drawer>
  );
}
